import { memo, useCallback, useDeferredValue, useMemo, useState, type ReactNode } from "react";

import {
  Button,
  DrawerBody,
  DrawerHeader,
  Input,
  makeStyles,
  mergeClasses,
  OverlayDrawer,
  Switch,
  Tab,
  TabList,
  tokens,
  Tooltip,
} from "@fluentui/react-components";
import {
  CursorClick20Regular,
  Dismiss20Regular,
  Lightbulb16Regular,
  Map20Regular,
  Play20Regular,
  Search16Regular,
  Target20Regular,
} from "@fluentui/react-icons";

import { HELP_PANEL_ATTR, locateElement } from "./dom";
import { HelpAsk } from "./HelpAsk";
import type { HelpNotice } from "./HelpLauncher";
import type { HelpLanguage, HelpStep, PageHelp } from "./types";
import {
  groupStepsBySection,
  localizedContext,
  localizedPage,
  localizedSection,
  localizedStep,
  roleBadgeLabel,
  stepMatchesQuery,
} from "./types";
import { DRAWER_WIDTH, uiText, Z } from "./ui";

/**
 * The readable guide for the current screen, including the parts behind a dialog: the tour shows
 * only what is on screen, the drawer deliberately does not, so a field inside the enrol dialog is
 * still readable without opening it.
 */

/** How much of the panel stays on screen while it stands aside, so it never reads as closed. */
const YIELD_SLIVER = 14;

const useStyles = makeStyles({
  surface: {
    width: `${DRAWER_WIDTH}px`,
    maxWidth: "100vw",
    // The CSS `translate` property, not `transform`: Fluent's drawer motion animates `transform`
    // through the Web Animations API, which would win over an inline transform. The two compose,
    // so the peek works whatever state the open animation left behind.
    transitionProperty: "translate",
    transitionDuration: "220ms",
    transitionTimingFunction: "ease",
  },
  backdrop: {
    transitionProperty: "background-color",
    transitionDuration: "180ms",
  },
  header: {
    flexDirection: "column",
    alignItems: "stretch",
    paddingBottom: "12px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  titleRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  eyebrow: {
    fontSize: "10px",
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    color: tokens.colorNeutralForegroundDisabled,
  },
  title: {
    fontSize: "18px",
    fontWeight: tokens.fontWeightBold,
    lineHeight: 1.25,
    marginTop: "2px",
  },
  tabs: { marginTop: "8px", marginLeft: "-10px" },
  askBody: { display: "flex", flexDirection: "column", padding: 0, overflow: "hidden" },
  intro: {
    fontSize: "12.5px",
    color: tokens.colorNeutralForeground2,
    lineHeight: 1.6,
    marginTop: "12px",
  },
  buttons: { display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "14px" },
  hint: { fontSize: "11.5px", color: tokens.colorNeutralForegroundDisabled, marginTop: "6px" },
  search: { width: "100%", marginTop: "12px" },
  body: { paddingBottom: "24px" },
  matchLine: {
    fontSize: "11.5px",
    color: tokens.colorNeutralForegroundDisabled,
    marginTop: "12px",
  },
  sectionRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginTop: "20px",
    marginBottom: "8px",
  },
  sectionLabel: {
    fontSize: "10px",
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    color: tokens.colorNeutralForegroundDisabled,
    whiteSpace: "nowrap",
  },
  rule: { flex: 1, height: "1px", backgroundColor: tokens.colorNeutralStroke2 },
  card: {
    display: "flex",
    gap: "8px",
    alignItems: "flex-start",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "12px",
    padding: "12px",
    marginBottom: "8px",
    backgroundColor: tokens.colorNeutralBackground1,
  },
  cardText: { flex: 1, minWidth: 0 },
  cardTitleRow: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px" },
  cardTitle: { fontSize: "13.5px", fontWeight: tokens.fontWeightSemibold },
  cardBody: {
    fontSize: "12.5px",
    color: tokens.colorNeutralForeground2,
    marginTop: "4px",
    lineHeight: 1.55,
  },
  cardHint: {
    fontSize: "11.5px",
    color: tokens.colorNeutralForegroundDisabled,
    marginTop: "6px",
    fontStyle: "italic",
    lineHeight: 1.5,
  },
  cardHintMissing: { color: tokens.colorPaletteDarkOrangeForeground1 },
  cardActions: { display: "flex", flexDirection: "column", flexShrink: 0 },
  pill: {
    fontSize: "10px",
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    color: "#9a5a00",
    backgroundColor: "#fff1d6",
    borderRadius: "999px",
    padding: "1px 6px",
    whiteSpace: "nowrap",
  },
  tip: { display: "flex", gap: "8px", alignItems: "flex-start", marginBottom: "10px" },
  tipIcon: { color: tokens.colorBrandForeground1, flexShrink: 0, marginTop: "2px" },
  tipText: { fontSize: "12.5px", color: tokens.colorNeutralForeground2, lineHeight: 1.55 },
  footer: {
    padding: "10px 20px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
    flexShrink: 0,
  },
  footerCaption: {
    fontSize: "11px",
    color: tokens.colorNeutralForegroundDisabled,
    lineHeight: 1.5,
  },
});

const SectionLabel = ({ children }: { children: ReactNode }) => {
  const styles = useStyles();
  return (
    <div className={styles.sectionRow}>
      <span className={styles.sectionLabel}>{children}</span>
      <span className={styles.rule} />
    </div>
  );
};

const StepCardBody = ({
  step,
  language,
  contextHint,
  onLocate,
  onStartHere,
}: {
  step: HelpStep;
  language: HelpLanguage;
  /** How to reveal the control, when the step lives behind a UI action. */
  contextHint?: string;
  /** Flashes the control and reports whether it was on screen. */
  onLocate: (step: HelpStep) => boolean;
  /** Opens the tour ON this step, so a card found by searching is one click from the walk. */
  onStartHere: (step: HelpStep) => void;
}) => {
  const styles = useStyles();
  // Only set after a failed locate: the drawer does not probe the DOM up front, so a step is
  // never greyed out on a stale measurement taken before the user acted.
  const [missing, setMissing] = useState(false);
  const badge = roleBadgeLabel(step);
  const { title, body, translated } = localizedStep(step, language);
  const text = uiText(language);

  return (
    <div className={styles.card}>
      <div className={styles.cardText}>
        <div className={styles.cardTitleRow}>
          <span className={styles.cardTitle}>{title}</span>
          {badge && <span className={styles.pill}>{badge}</span>}
          {!translated && <span className={styles.pill}>EN</span>}
        </div>
        <div className={styles.cardBody}>{body}</div>
        {(missing || step.context) && contextHint && (
          <div className={mergeClasses(styles.cardHint, missing && styles.cardHintMissing)}>
            {contextHint}
          </div>
        )}
        {missing && !contextHint && (
          <div className={mergeClasses(styles.cardHint, styles.cardHintMissing)}>
            {text.notOnScreen}
          </div>
        )}
      </div>
      <div className={styles.cardActions}>
        <Tooltip content={text.locate} relationship="description" positioning="before">
          <Button
            appearance="subtle"
            size="small"
            icon={<Target20Regular />}
            aria-label={text.locateAria(title)}
            onClick={() => setMissing(!onLocate(step))}
          />
        </Tooltip>
        <Tooltip content={text.startHere} relationship="description" positioning="before">
          <Button
            appearance="subtle"
            size="small"
            icon={<Play20Regular />}
            aria-label={text.startHereAria(title)}
            onClick={() => onStartHere(step)}
          />
        </Tooltip>
      </div>
    </div>
  );
};

/**
 * Memoised because of the search box: typing a character would otherwise re-render every card
 * (each with two tooltips) before the character is painted. Props are stable per card.
 */
const StepCard = memo(StepCardBody);
StepCard.displayName = "StepCard";

interface HelpDrawerProps {
  open: boolean;
  help: PageHelp;
  /** Role-filtered steps — the same list the launcher hands to the tour. */
  steps: HelpStep[];
  /** Whether the floating "?" is shown over the page (see help/preference.ts). */
  floating: boolean;
  onFloatingChange: (on: boolean) => void;
  /** The reader's language — follows the app's language switch. */
  language: HelpLanguage;
  onClose: () => void;
  /** Starts the tour — from the beginning, or at one step when the reader picked one. */
  onStartTour: (from?: HelpStep) => void;
  /** Hands over to click-to-explain. */
  onInspect: () => void;
  /** Where the Ask tab's cross-page notices go — the app's toasts, via the launcher. */
  notify: (notice: HelpNotice) => void;
}

export const HelpDrawer = ({
  open,
  help,
  steps,
  floating,
  onFloatingChange,
  language,
  onClose,
  onStartTour,
  onInspect,
  notify,
}: HelpDrawerProps) => {
  const styles = useStyles();
  const text = uiText(language);
  /**
   * Filters the entries on this page; matching runs over the BODIES too. A deferred value rather
   * than a debounce: there is no request to spare, only rendering, so the field stays urgent and
   * the list follows as an interruptible second pass.
   */
  /**
   * "This page" answers "what is this control?"; "Ask" answers "where do I do X?" for any page.
   * Component state in a drawer that stays mounted, so the tab survives close / reopen.
   */
  const [tab, setTab] = useState<"page" | "ask">("page");
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const matches = useMemo(
    () => steps.filter((step) => stepMatchesQuery(step, deferredQuery)),
    [steps, deferredQuery]
  );
  const groups = useMemo(
    () => groupStepsBySection(help.sections, matches),
    [help.sections, matches]
  );
  /**
   * Set while the panel stands aside so a control underneath it can be seen. Not a close: the
   * scroll position and the query survive, and it slides back on its own once the flash fades.
   */
  const [yielding, setYielding] = useState(false);
  const page = localizedPage(help, language);

  // Stable by contract: an identity that changed per render would defeat the memo on every card.
  const locate = useCallback(
    (step: HelpStep): boolean => locateElement(step, { onYield: setYielding }),
    []
  );

  return (
    <OverlayDrawer
      open={open}
      position="end"
      onOpenChange={(_event, data) => {
        if (!data.open) onClose();
      }}
      className={styles.surface}
      style={{
        zIndex: Z.drawer,
        translate: yielding ? `calc(100% - ${YIELD_SLIVER}px) 0` : undefined,
      }}
      // Marks this as the guide's own panel, not the dialog a tour should stay inside.
      {...{ [HELP_PANEL_ATTR]: "drawer" }}
      backdrop={{
        className: styles.backdrop,
        // Full contrast for the flash: the scrim would dim the very control being pointed at.
        style: { zIndex: Z.drawer, ...(yielding ? { backgroundColor: "transparent" } : {}) },
      }}
    >
      <DrawerHeader className={styles.header}>
        <div className={styles.titleRow}>
          <div>
            <div className={styles.eyebrow}>{text.eyebrow}</div>
            <div className={styles.title}>{page.title}</div>
          </div>
          <Button
            appearance="subtle"
            size="small"
            icon={<Dismiss20Regular />}
            aria-label={text.close}
            onClick={onClose}
          />
        </div>

        <TabList
          className={styles.tabs}
          size="small"
          selectedValue={tab}
          onTabSelect={(_event, data) => setTab(data.value as "page" | "ask")}
        >
          <Tab value="page" data-help="help-tab-page">
            {text.tabPage}
          </Tab>
          <Tab value="ask" data-help="help-tab-ask">
            {text.tabAsk}
          </Tab>
        </TabList>

        {tab === "page" && (
          <>
            <div className={styles.intro}>{page.intro}</div>

            <div className={styles.buttons}>
              <Button
                appearance="primary"
                size="small"
                icon={<Map20Regular />}
                onClick={() => onStartTour()}
                data-help="help-tour-button"
              >
                {text.tour}
              </Button>
              {/* The way out of a long walk: the reader who wants ONE field points at it. */}
              <Button
                appearance="outline"
                size="small"
                icon={<CursorClick20Regular />}
                onClick={onInspect}
                data-help="help-inspect-button"
              >
                {text.inspect}
              </Button>
            </div>
            <div className={styles.hint}>{text.hint}</div>

            <Input
              className={styles.search}
              size="small"
              value={query}
              onChange={(_event, data) => setQuery(data.value)}
              placeholder={text.searchPlaceholder(steps.length)}
              contentBefore={<Search16Regular />}
              maxLength={60}
              aria-label={text.searchAria}
              data-help="help-page-search"
            />
          </>
        )}
      </DrawerHeader>

      {tab === "ask" ? (
        <DrawerBody className={styles.askBody}>
          <HelpAsk
            language={language}
            // Navigating away leaves the guide describing a page the user has left.
            onNavigated={onClose}
            onYield={setYielding}
            notify={notify}
          />
        </DrawerBody>
      ) : (
        <DrawerBody className={styles.body}>
          {deferredQuery.length > 0 && (
            <div className={styles.matchLine}>
              {matches.length === 0
                ? text.noMatch
                : text.matchLine(matches.length, steps.length, deferredQuery)}
            </div>
          )}

          {groups.map((group) => (
            <div key={group.section}>
              <SectionLabel>{localizedSection(help, group.section, language)}</SectionLabel>
              {group.steps.map((step) => (
                <StepCard
                  key={step.selector}
                  step={step}
                  language={language}
                  contextHint={
                    step.context ? localizedContext(help, step.context, language) : undefined
                  }
                  onLocate={locate}
                  onStartHere={onStartTour}
                />
              ))}
            </div>
          ))}

          {/* Hidden while filtering: the tips answer no search. */}
          {deferredQuery.length === 0 && page.tips && page.tips.length > 0 && (
            <>
              <SectionLabel>{text.goodToKnow}</SectionLabel>
              {page.tips.map((tip) => (
                <div key={tip} className={styles.tip}>
                  <Lightbulb16Regular className={styles.tipIcon} />
                  <span className={styles.tipText}>{tip}</span>
                </div>
              ))}
            </>
          )}
        </DrawerBody>
      )}

      <div className={styles.footer}>
        <Switch
          checked={floating}
          onChange={(_event, data) => onFloatingChange(data.checked)}
          label={text.floatingLabel}
          aria-label={text.floatingSwitchAria}
        />
        <div className={styles.footerCaption}>{floating ? text.floatingOn : text.floatingOff}</div>
      </div>
    </OverlayDrawer>
  );
};
